import useUserRole from "hooks/useUserRole";
import type { ReactNode } from "react";

import { ROLES } from "./roles.enum";

interface IScopesContext {
    /**
     * The component wrapped by the scope.
     * @param extraProps
     * @returns
     */
    children: (extraProps?: Record<string, any>) => ReactNode;
    /**
     * The error component to display if needed.
     */
    renderError?: JSX.Element;
    /**
     * Props to pass to children if permission isn't granted. Ex: {disabled: true}
     */
    errorProps?: Record<string, any>;
    /**
     * Scopes that have all permission.
     */
    scopes?: TScope[];
    /**
     * Extra conditions outside of scopes: for instance, action allowed if no async imports pending.
     * All conditions must be true for everything to work as it should.
     */
    extraConditions?: boolean[];
    /**
     * One data type instead of specific scope. Ex: DATA_SCOPES for all actions on Beneficiary/Title/etc. pages.
     */
    scopeType?: Record<string, TScope>;
}

/**
 * Container to control components depending on scope.
 * So for instance, we can wrap a button around it to disable (or other) dependig on role scope or other condition.
 */
const ScopeContext = ({
    children,
    renderError,
    errorProps,
    scopes = [],
    extraConditions = [],
    scopeType,
}: IScopesContext) => {
    const { userRole } = useUserRole();
    const permissions = PERMISSIONS[userRole ?? ROLES.OWNER];

    const permissionGranted = hasPermission({ permissions, scopes, extraConditions, scopeType });

    // If permission denied and no errorProps passed, display renderError component.
    if (!permissionGranted && !errorProps) return <>{renderError || <div>Permission denied</div>}</>;

    // If permission denied and errorProps passed, display children with props.
    if (!permissionGranted && errorProps) {
        return <>{children(errorProps)}</>;
    }

    // If permission given, display component as is.
    return <>{children()}</>;
};

export default ScopeContext;

const hasPermission = ({ permissions, scopes, extraConditions, scopeType }) => {
    // If all conditions are true, allow.
    let extraConditionsPassed = true;
    if (extraConditions.length) {
        extraConditionsPassed = extraConditions.every((condition) => condition === true);
    }
    // If only scope type passed, check if there is at least one scope within that type that exists in role permissions. If so, allow:
    let scopeTypesPassed = true;
    if (scopeType) {
        scopeTypesPassed = Object.keys(scopeType).some((key) => permissions.includes(scopeType[key]));
    }

    let scopesPassed = true;
    // If at least one scope exists inside role permissions, allow:
    if (scopes.length) {
        scopesPassed = permissions.some((permission) => scopes.includes(permission));
    }

    return extraConditionsPassed && scopeTypesPassed && scopesPassed;
};

type TRoles = Record<string, ROLES>;

export enum AGREEMENTS_ROLES {
    AGREEMENT_ADMIN = "agreement_admin",
    AGREEMENT_MANAGER = "agreement_manager",
    AGREEMENT_CONTRIBUTOR = "agreement_contributor",
}

export const AGREEMENTS_ROLES_ALL = Object.values(AGREEMENTS_ROLES);
export const ROLES_ALL = Object.values(ROLES);

type TScope =
    | "addSale"
    | "deleteSale"
    | "sendEmail"
    | "importData"
    | "view"
    | "createData"
    | "deleteData"
    | "editData"
    | "viewData";
export const MANAGEMENT_SCOPES: Record<string, TScope> = {
    ADD_SALE: "addSale",
    DELETE_SALE: "deleteSale",
    SEND_EMAIL: "sendEmail",
    IMPORT: "importData",
    VIEW: "view",
};

export const DATA_SCOPES: Record<string, TScope> = {
    CREATE: "createData",
    DELETE: "deleteData",
    EDIT: "editData",
    VIEW: "viewData",
    IMPORT: "importData",
};

export const PERMISSIONS: Record<keyof TRoles, TScope[]> = {
    [ROLES.NONE]: [],
    [ROLES.VIEW_ONLY]: [MANAGEMENT_SCOPES.VIEW],
    [ROLES.OWNER]: [
        MANAGEMENT_SCOPES.ADD_SALE,
        MANAGEMENT_SCOPES.DELETE_SALE,
        MANAGEMENT_SCOPES.SEND_EMAIL,
        MANAGEMENT_SCOPES.IMPORT,
        MANAGEMENT_SCOPES.VIEW,
        DATA_SCOPES.CREATE,
        "importData",
    ],
    [ROLES.ACCOUNTANT]: [],
    [ROLES.STOCK_MANAGER]: [],
    [ROLES.AGREEMENT_ADMIN]: [],
    [ROLES.AGREEMENT_MANAGER]: [],
    [ROLES.AGREEMENT_CONTRIBUTOR]: [],
};
